import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createWhiteLabel } from '@/api/white-labels';
import type { CreateWhiteLabelData } from '@/types/white-label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tag, ArrowLeft, Save } from 'lucide-react';

export function WhiteLabelFormPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateWhiteLabelData>({
    name: '',
    host: '',
    urlAndroid: '',
    urlIOS: '',
    description: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Le nom est requis');
      return;
    }
    if (!formData.host.trim()) {
      setError('Le domaine (host) est requis');
      return;
    }

    try {
      setLoading(true);
      const result = await createWhiteLabel({
        name: formData.name.trim(),
        host: formData.host.trim(),
        urlAndroid: formData.urlAndroid?.trim() || undefined,
        urlIOS: formData.urlIOS?.trim() || undefined,
        description: formData.description?.trim() || undefined,
      });
      navigate(`/white-labels/${result.id}`);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la creation';
      setError(errorMessage);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/white-labels')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Tag className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Nouvelle marque blanche
            </h1>
            <p className="text-sm text-muted-foreground">
              Creer une marque blanche avec son tenant group V2.
            </p>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            &times;
          </button>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="max-w-xl space-y-6">
        <div className="rounded-lg border bg-card p-6 space-y-4">
          <h2 className="font-semibold text-lg">Informations requises</h2>

          <div className="space-y-2">
            <Label htmlFor="name">Nom *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ex: Mon École Premium"
              required
            />
            <p className="text-xs text-muted-foreground">
              Le nom de la marque blanche (sera aussi le nom du tenant group V2)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="host">Domaine (Host) *</Label>
            <Input
              id="host"
              value={formData.host}
              onChange={(e) => setFormData({ ...formData, host: e.target.value })}
              placeholder="Ex: mon-ecole.ecole-futee.com"
              required
            />
            <p className="text-xs text-muted-foreground">
              Le domaine du tenant group V2 (sans https://)
            </p>
          </div>
        </div>

        <div className="rounded-lg border bg-card p-6 space-y-4">
          <h2 className="font-semibold text-lg">Informations optionnelles</h2>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description de la marque blanche..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="urlAndroid">URL Application Android</Label>
            <Input
              id="urlAndroid"
              type="url"
              value={formData.urlAndroid}
              onChange={(e) => setFormData({ ...formData, urlAndroid: e.target.value })}
              placeholder="https://play.google.com/store/apps/..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="urlIOS">URL Application iOS</Label>
            <Input
              id="urlIOS"
              type="url"
              value={formData.urlIOS}
              onChange={(e) => setFormData({ ...formData, urlIOS: e.target.value })}
              placeholder="https://apps.apple.com/..."
            />
          </div>
        </div>

        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/white-labels')}>
            Annuler
          </Button>
          <Button type="submit" disabled={loading}>
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Creation...' : 'Creer la marque blanche'}
          </Button>
        </div>
      </form>
    </div>
  );
}
